import { Check, ChevronsUpDown, Plus } from 'lucide-react'
import { useNavigate } from 'react-router'

import type { MyOrg } from '@/api/types'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from '@/components/ui/sidebar'
import { ROLE_LABEL } from '@/lib/labels'
import { rememberOrgSlug } from '@/lib/lastOrg'

const OrgAvatar = ({ org }: { org: MyOrg }) =>
  org.logoUrl ? (
    <img src={org.logoUrl} alt="" className="size-8 rounded-full object-cover" />
  ) : (
    <span className="flex size-8 items-center justify-center rounded-full bg-primary text-sm font-medium text-primary-foreground">
      {org.name.slice(0, 1).toUpperCase()}
    </span>
  )

/** The current org at the top of the sidebar; opens a list of the person's orgs and "Create an org". */
export const OrgSwitcher = ({ org, orgs }: { org: MyOrg; orgs: MyOrg[] }) => {
  const navigate = useNavigate()
  const open = (target: MyOrg) => {
    rememberOrgSlug(target.slug)
    void navigate(`/o/${target.slug}`)
  }
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton size="lg">
              <OrgAvatar org={org} />
              <span className="grid flex-1 text-left leading-tight">
                <span className="truncate font-medium">{org.name}</span>
                <span className="truncate text-xs text-muted-foreground">{ROLE_LABEL[org.role]}</span>
              </span>
              <ChevronsUpDown className="ml-auto size-4 text-muted-foreground" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64">
            <DropdownMenuLabel className="text-xs text-muted-foreground">Your orgs</DropdownMenuLabel>
            {orgs.map((candidate) => (
              <DropdownMenuItem key={candidate.id} onSelect={() => open(candidate)} className="gap-2">
                <OrgAvatar org={candidate} />
                <span className="flex-1 truncate">{candidate.name}</span>
                {candidate.id === org.id && <Check className="size-4" />}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => void navigate('/orgs/new')} className="gap-2">
              <Plus className="size-4" /> Create an org
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
